import type { CostActual, ISODate, OrganizationTeam, Project, RaciAssignment, RaciLetter, Stakeholder, StakeholderLevel, Task, UserResource, WeeklyReportDraft, WeeklyReportSnapshot, WeeklySectionVisibility } from "@/engine/types";
import { normalizeWeeklyNote, type UserWeeklyNote } from "@/features/user-weekly/user-weekly-model";
import { isISODate } from "@/engine/date";
import type { ReportPeriod } from "@/features/reports/report-model";
import { defaultCostOptions, sampleOrganizationTeams, sampleUserResources } from "@/engine/sample";
import { TEAM_ICONS } from "@/features/resources/team-appearance";
import { SAMPLE_JOINED_WORKSPACE, SEED_WORKSPACE_META, seedWorkspaceFace } from "@/engine/workspace-seeds";
import { normalizePlanDocuments } from "@/features/docs/doc-model";
import { normalizeProjectTemplates, normalizeRetrospective } from "@/features/retrospective/retrospective-model";
import { normalizeWorkspaceDocTemplates } from "@/features/docs/doc-templates";
import { normalizeExecution } from "./execution-task-ops";
import { normalizeIntakes } from "./intake-ops";
import { normalizeRecords } from "./record-ops";
import {
  normalizeOwnerField,
  normalizeWorkspaceIcon,
  normalizeWorkspaceImageUrl,
  normalizeWorkspaceRole,
} from "@/features/workspace/workspace-face";
import { resolveCalendar } from "@/engine/holiday-catalogs";
import { refillWorkspace, seedWorkspaceRecords, type WorkspaceRecord } from "./workspace-ops";

export type { WorkspaceRecord };

export const STORAGE_V1 = "wavex-plan:v1";
export const STORAGE_V2 = "wavex-plan:v2";
export const STORAGE_V3 = "wavex-plan:v3";

/** 기본 세 워크스페이스를 최근 목업으로 다시 채우는 세대. 사용자가 만든 워크스페이스는 건드리지 않는다. */
export const SEED_REVISION = 11;

export type ThemePref = "system" | "light" | "dark";

/** Local identity used for "내 작업", comment authorship and activity logs (no server accounts yet). */
export interface UserProfile {
  displayName: string;
  /** 계정에 적는 직함. 인력 자원의 역할과 다르다. */
  title?: string;
  /** 표시용 소속. */
  affiliation?: string;
  /** 정사각으로 줄인 사진. 없으면 이니셜. 이 브라우저에만 있다. */
  avatar?: string;
  /** 로그인이 생기면 읽기 전용으로 채운다. 지금은 목업 사례로만 보인다. */
  email?: string;
}

/** `reports`는 사이드바에서 「현황」으로 보인다(옛 요약·리포트를 합친 화면). 내부 id는 저장된 상태와 링크 호환을 위해 그대로 둔다. */
export type ProjectTab = "schedule" | "reports" | "tasks" | "records" | "docs" | "files" | "calendar" | "team" | "budget" | "stakeholders" | "weekly" | "review" | "activity" | "intake";

export interface ProjectChrome {
  checkpoints: Array<{ id: string; name: string; createdAt: string; project: Project }>;
  /** closed report periods, newest first */
  reports?: ReportPeriod[];
  collapsed: string[];
  ui?: unknown;
  /** last project tab the user was on; used by "최근" links */
  lastTab?: ProjectTab;
}

export interface GlobalUi {
  theme: ThemePref;
  profile: UserProfile;
  /** last assignee filter used on the global "내 작업" page ("" = everyone) */
  myTasksAssignee?: string;
  /** app-wide left nav (AppNav) is collapsed */
  navCollapsed?: boolean;
  /** 개인 주간 보고. 키는 사람 이름과 주 시작일. 값은 빼기 목록과 한 줄 메모. */
  userWeekly?: Record<string, UserWeeklyNote>;
}

export interface PersistedV2 {
  version: 2;
  activeProjectId: string | null;
  projects: Record<string, Project>;
  byProject: Record<string, ProjectChrome>;
  globalUi: GlobalUi;
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
}

export interface PersistedV3 {
  version: 3;
  activeWorkspaceId: string;
  workspaces: Record<string, WorkspaceRecord>;
  globalUi: GlobalUi;
  /** 없으면 예전 목업이다. SEED_REVISION에 닿으면 기본 워크스페이스를 다시 채우지 않는다. */
  seedRevision?: number;
  /** 참여 목업을 한 번 넣은 뒤. 지우면 다시 만들지 않는다. */
  sampleJoinedWorkspace?: boolean;
}

export interface PersistedV1 {
  project: Project;
  checkpoints?: ProjectChrome["checkpoints"];
  ui?: Record<string, unknown>;
  collapsed?: string[];
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const DEFAULT_PROFILE: UserProfile = { displayName: "나" };

export function defaultResources(): Record<string, UserResource> {
  const map: Record<string, UserResource> = {};
  for (const r of sampleUserResources()) map[r.id] = r;
  return map;
}

export function defaultTeams(): Record<string, OrganizationTeam> {
  const map: Record<string, OrganizationTeam> = {};
  for (const t of sampleOrganizationTeams()) map[t.id] = t;
  return map;
}

export function emptyCatalog(): PersistedV2 {
  return {
    version: 2,
    activeProjectId: null,
    projects: {},
    byProject: {},
    globalUi: { theme: "system", profile: { ...DEFAULT_PROFILE } },
    resources: defaultResources(),
    teams: defaultTeams(),
  };
}

export function seedCatalog(): PersistedV3 {
  const workspaces = normalizeWorkspaces(seedWorkspaceRecords());
  return {
    version: 3,
    activeWorkspaceId: SEED_WORKSPACE_META[0]!.id,
    workspaces,
    globalUi: { theme: "system", profile: { ...DEFAULT_PROFILE } },
    seedRevision: SEED_REVISION,
    sampleJoinedWorkspace: true,
  };
}

export function isThemePref(v: unknown): v is ThemePref {
  return v === "system" || v === "light" || v === "dark";
}

const TABS: ProjectTab[] = ["schedule", "reports", "tasks", "records", "docs", "files", "calendar", "team", "budget", "stakeholders", "weekly", "review", "activity", "intake"];
export function isProjectTab(v: unknown): v is ProjectTab {
  return typeof v === "string" && (TABS as string[]).includes(v);
}

/** 사라진 탭 id가 저장 상태에 남아 있으면 흡수된 화면으로 옮긴다(board·analytics → 작업, overview → 현황). */
export function normalizeProjectTab(v: unknown): ProjectTab | undefined {
  if (v === "board" || v === "analytics") return "tasks";
  if (v === "overview" || v === "status") return "reports";
  return isProjectTab(v) ? v : undefined;
}

function profileText(value: unknown, max: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim().slice(0, max);
  return text || undefined;
}

function normalizeGlobalUi(raw: unknown): GlobalUi {
  const r = (raw ?? {}) as Partial<GlobalUi> & { profile?: Partial<UserProfile> };
  const name = profileText(r.profile?.displayName, 40) ?? DEFAULT_PROFILE.displayName;
  const title = profileText(r.profile?.title, 40);
  const affiliation = profileText(r.profile?.affiliation, 40);
  const email = profileText(r.profile?.email, 80);
  const avatar = typeof r.profile?.avatar === "string" && r.profile.avatar.startsWith("data:image/") && r.profile.avatar.length <= 120_000
    ? r.profile.avatar
    : undefined;
  return {
    theme: isThemePref(r.theme) ? r.theme : "system",
    profile: {
      displayName: name,
      ...(title ? { title } : {}),
      ...(affiliation ? { affiliation } : {}),
      ...(email ? { email } : {}),
      ...(avatar ? { avatar } : {}),
    },
    ...(typeof r.myTasksAssignee === "string" ? { myTasksAssignee: r.myTasksAssignee } : {}),
    ...(r.navCollapsed === true ? { navCollapsed: true } : {}),
    ...(() => {
      const userWeekly = normalizeUserWeeklyMap(r.userWeekly);
      return userWeekly ? { userWeekly } : {};
    })(),
  };
}

function normalizeUserWeeklyMap(raw: unknown): Record<string, UserWeeklyNote> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, UserWeeklyNote> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key.trim()) continue;
    const note = normalizeWeeklyNote(value);
    if (!note.excludedIds.length && !note.memo) continue;
    out[key] = note;
  }
  return Object.keys(out).length ? out : undefined;
}

const LEVELS = new Set<StakeholderLevel>(["low", "medium", "high"]);
const LETTERS = new Set<RaciLetter>(["R", "A", "C", "I"]);

function normalizeStakeholders(raw: unknown): Stakeholder[] {
  if (!Array.isArray(raw)) return [];
  const out: Stakeholder[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<Stakeholder>;
    const name = typeof row.name === "string" ? row.name.trim() : "";
    if (typeof row.id !== "string" || !row.id || !name) continue;
    if (!LEVELS.has(row.interest as StakeholderLevel) || !LEVELS.has(row.influence as StakeholderLevel)) continue;
    out.push({
      id: row.id,
      name,
      org: typeof row.org === "string" ? row.org.trim() : "",
      role: typeof row.role === "string" ? row.role.trim() : "",
      interest: row.interest as StakeholderLevel,
      influence: row.influence as StakeholderLevel,
    });
  }
  return out;
}

function normalizeRaci(raw: unknown, people: Set<string>, tasks: Set<string>): RaciAssignment[] {
  if (!Array.isArray(raw)) return [];
  const out: RaciAssignment[] = [];
  const accountable = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<RaciAssignment>;
    if (typeof row.stakeholderId !== "string" || typeof row.taskId !== "string" || !LETTERS.has(row.letter as RaciLetter)) continue;
    if (!people.has(row.stakeholderId) || !tasks.has(row.taskId)) continue;
    if (row.letter === "A") {
      if (accountable.has(row.taskId)) continue;
      accountable.add(row.taskId);
    }
    out.push({ stakeholderId: row.stakeholderId, taskId: row.taskId, letter: row.letter as RaciLetter });
  }
  return out;
}

function normalizeCostActuals(raw: unknown, tasks: Set<string>): CostActual[] {
  if (!Array.isArray(raw)) return [];
  const out: CostActual[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<CostActual>;
    if (typeof row.id !== "string" || !row.id) continue;
    if (!isISODate(row.periodStart) || !isISODate(row.periodEnd) || row.periodEnd < row.periodStart) continue;
    if (typeof row.amount !== "number" || !Number.isFinite(row.amount) || row.amount < 0) continue;
    const taskId = typeof row.taskId === "string" && tasks.has(row.taskId) ? row.taskId : undefined;
    out.push({
      id: row.id,
      periodStart: row.periodStart,
      periodEnd: row.periodEnd,
      amount: row.amount,
      ...(taskId ? { taskId } : {}),
      ...(typeof row.note === "string" && row.note.trim() ? { note: row.note.trim() } : {}),
    });
  }
  return out;
}

function normalizeWeeklySnapshots(raw: unknown): WeeklyReportSnapshot[] {
  if (!Array.isArray(raw)) return [];
  const out: WeeklyReportSnapshot[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<WeeklyReportSnapshot>;
    if (typeof row.id !== "string" || typeof row.weekId !== "string" || !isISODate(row.statusDate) || typeof row.closedAt !== "string") continue;
    const version = typeof row.version === "number" && Number.isFinite(row.version) && row.version >= 1 ? Math.floor(row.version) : undefined;
    const dedupKey = version && version > 1 ? `${row.weekId}:v${version}` : row.weekId;
    if (seen.has(dedupKey)) continue;
    const document = row.document;
    if (!document || typeof document !== "object" || typeof document.report !== "object" || !document.report) continue;
    seen.add(dedupKey);
    out.push({
      id: row.id,
      weekId: row.weekId,
      statusDate: row.statusDate,
      periodLabel: typeof row.periodLabel === "string" ? row.periodLabel : "",
      closedAt: row.closedAt,
      ...(version ? { version } : {}),
      document: {
        capturedAt: typeof document.capturedAt === "string" ? document.capturedAt : row.closedAt,
        weekId: typeof document.weekId === "string" ? document.weekId : row.weekId,
        statusDate: typeof document.statusDate === "string" ? document.statusDate : row.statusDate,
        report: document.report as Record<string, unknown>,
      },
    });
  }
  return out;
}

function normalizeWeeklySectionDefaults(raw: unknown): WeeklySectionVisibility | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Partial<WeeklySectionVisibility>;
  return {
    cost: r.cost === true,
    wbs: r.wbs === true,
    history: r.history !== false,
  };
}

function normalizeWeeklyDrafts(raw: unknown): Record<ISODate, WeeklyReportDraft> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<ISODate, WeeklyReportDraft> = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (!isISODate(key) || !val || typeof val !== "object") continue;
    const d = val as Partial<WeeklyReportDraft>;
    if (typeof d.id !== "string" || !isISODate(d.statusDate) || typeof d.title !== "string") continue;
    out[key] = d as WeeklyReportDraft;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeTaskCosts(tasks: Task[]): Task[] {
  return tasks.map((task) => {
    if (typeof task.actualCost !== "number" || !Number.isFinite(task.actualCost) || task.actualCost < 0) {
      if (task.actualCost === undefined) return task;
      const { actualCost: _drop, ...rest } = task;
      return rest;
    }
    return task;
  });
}

/** Ensure project has valid records, resource assignments, solutions and budget structures. */
export function normalizeProjectResources(p: Project): Project {
  const records = normalizeRecords(p);
  const docs = normalizePlanDocuments({ ...p, records });
  const tasks = normalizeTaskCosts(p.tasks);
  const stakeholders = normalizeStakeholders(p.stakeholders);
  const people = new Set(stakeholders.map((person) => person.id));
  const taskIds = new Set(tasks.map((task) => task.id));
  const weeklyDrafts = normalizeWeeklyDrafts(p.weeklyDrafts);
  const weeklySectionDefaults = normalizeWeeklySectionDefaults(p.weeklySectionDefaults);
  const retrospective = normalizeRetrospective(p.retrospective);
  const { retrospective: _rawRetro, ...rest } = p;
  return {
    ...rest,
    ...(retrospective ? { retrospective } : {}),
    tasks,
    records,
    intakes: normalizeIntakes(p.intakes),
    ...docs,
    stakeholders,
    raci: normalizeRaci(p.raci, people, taskIds),
    costActuals: normalizeCostActuals(p.costActuals, taskIds),
    weeklySnapshots: normalizeWeeklySnapshots(p.weeklySnapshots),
    ...(weeklyDrafts ? { weeklyDrafts } : {}),
    ...(weeklySectionDefaults ? { weeklySectionDefaults } : {}),
    calendar: p.calendar ? resolveCalendar(p.calendar) : p.calendar,
    resourceAssignments: Array.isArray(p.resourceAssignments) ? p.resourceAssignments : [],
    solutions: Array.isArray(p.solutions) ? p.solutions : [],
    budget: p.budget && typeof p.budget === "object"
      ? {
          totalBudget: typeof p.budget.totalBudget === "number" ? p.budget.totalBudget : 0,
          targetLaborCost: typeof p.budget.targetLaborCost === "number" ? p.budget.targetLaborCost : 0,
          targetSolutionCost: typeof p.budget.targetSolutionCost === "number" ? p.budget.targetSolutionCost : 0,
          targetExpenseCost: typeof p.budget.targetExpenseCost === "number" ? p.budget.targetExpenseCost : 0,
          contingencyCost: typeof p.budget.contingencyCost === "number" ? p.budget.contingencyCost : 0,
          costOptions: p.budget.costOptions ?? defaultCostOptions(),
        }
      : {
          totalBudget: 0,
          targetLaborCost: 0,
          targetSolutionCost: 0,
          targetExpenseCost: 0,
          contingencyCost: 0,
          costOptions: defaultCostOptions(),
        },
  };
}

/** Make sure every stored project carries Planner and Resource data (older blobs predate it). */
export function normalizeProjects(projects: Record<string, Project>): Record<string, Project> {
  const out: Record<string, Project> = {};
  for (const [id, p] of Object.entries(projects)) {
    if (!p || !Array.isArray(p.tasks)) continue;
    const withExec = normalizeExecution(p) as Project;
    out[id] = normalizeProjectResources(withExec);
  }
  return out;
}

/** 샘플이 삭제된 계정으로 둔 사람은, 저장본에 값이 없을 때만 그 표시를 채운다. */
function mergeSampleAccountDeleted(resources: Record<string, UserResource>): Record<string, UserResource> {
  let next = resources;
  for (const sample of sampleUserResources()) {
    if (!sample.accountDeleted) continue;
    const cur = next[sample.id];
    if (!cur || cur.accountDeleted !== undefined) continue;
    if (next === resources) next = { ...resources };
    next[sample.id] = { ...cur, accountDeleted: true };
  }
  return next;
}

const TEAM_ICON_IDS = new Set(TEAM_ICONS.map((item) => item.id));

/** 샘플 팀에 아이콘이 없으면 목업 아이콘을 채운다. 이미 고른 아이콘은 둔다. */
function mergeSampleTeamIcons(teams: Record<string, OrganizationTeam>): Record<string, OrganizationTeam> {
  let next = teams;
  for (const sample of sampleOrganizationTeams()) {
    const cur = next[sample.id];
    if (!cur || !sample.icon || TEAM_ICON_IDS.has(cur.icon ?? "")) continue;
    if (next === teams) next = { ...teams };
    next[sample.id] = { ...cur, icon: sample.icon };
  }
  return next;
}

/** 그로스 마케팅 목업에 초대 중인 사람이 없으면 채운다. 이미 있는 저장본은 그대로 둔다. */
function mergeMarketingInvite(resources: Record<string, UserResource>, template: string): Record<string, UserResource> {
  if (template !== "marketing" || resources.res_cho_partner) return resources;
  const sample = sampleUserResources().find((person) => person.id === "res_cho_partner");
  if (!sample) return resources;
  return { ...resources, [sample.id]: sample };
}

function normalizeWorkspace(raw: Partial<WorkspaceRecord> & { id?: string }, fallbackId: string): WorkspaceRecord | null {
  const id = typeof raw.id === "string" && raw.id.trim() ? raw.id : fallbackId;
  const projects = normalizeProjects(raw.projects ?? {});
  const name = typeof raw.name === "string" && raw.name.trim() ? raw.name.trim() : "워크스페이스";
  const template = raw.template === "erp" || raw.template === "robot" || raw.template === "marketing" || raw.template === "empty" ? raw.template : "empty";
  const resources = mergeMarketingInvite(
    mergeSampleAccountDeleted(raw.resources && Object.keys(raw.resources).length > 0 ? raw.resources : {}),
    template,
  );
  const teams = raw.teams && Object.keys(raw.teams).length > 0 ? mergeSampleTeamIcons(raw.teams) : {};
  const face = seedWorkspaceFace(id);
  return {
    id,
    name,
    icon: normalizeWorkspaceIcon(raw.icon, face?.icon),
    color: typeof raw.color === "string" && raw.color.trim() ? raw.color : face?.color ?? "#0078d4",
    imageUrl: "imageUrl" in raw ? normalizeWorkspaceImageUrl(raw.imageUrl) : face?.imageUrl ?? null,
    description: typeof raw.description === "string" ? raw.description : face?.description ?? "",
    createdAt: typeof raw.createdAt === "string" && raw.createdAt ? raw.createdAt : new Date().toISOString(),
    template,
    role: "role" in raw ? normalizeWorkspaceRole(raw.role) : face?.role ?? "owner",
    ownerName: "ownerName" in raw ? normalizeOwnerField(raw.ownerName) : face?.ownerName ?? null,
    ownerEmail: "ownerEmail" in raw ? normalizeOwnerField(raw.ownerEmail) : face?.ownerEmail ?? null,
    projects,
    byProject: normalizeChrome(raw.byProject as Record<string, ProjectChrome> | undefined),
    resources,
    teams,
    docTemplates: normalizeWorkspaceDocTemplates(raw.docTemplates),
    projectTemplates: normalizeProjectTemplates(raw.projectTemplates),
    activeProjectId: raw.activeProjectId && projects[raw.activeProjectId] ? raw.activeProjectId : Object.keys(projects)[0] ?? null,
    ...(Array.isArray(raw.openPlanIds)
      ? { openPlanIds: raw.openPlanIds.filter((planId): planId is string => typeof planId === "string" && !!projects[planId]) }
      : {}),
  };
}

function normalizeWorkspaces(raw: Record<string, Partial<WorkspaceRecord>> | undefined): Record<string, WorkspaceRecord> {
  const out: Record<string, WorkspaceRecord> = {};
  for (const [id, ws] of Object.entries(raw ?? {})) {
    const next = normalizeWorkspace(ws ?? {}, id);
    if (next) out[next.id] = next;
  }
  return out;
}

export function migrateV2ToV3(data: PersistedV2): PersistedV3 {
  const id = "ws_migrated";
  const workspace: WorkspaceRecord = {
    id,
    name: "내 워크스페이스",
    icon: "folder",
    color: "#0078d4",
    imageUrl: null,
    description: "",
    createdAt: new Date().toISOString(),
    template: "empty",
    role: "owner",
    ownerName: null,
    ownerEmail: null,
    projects: data.projects,
    byProject: data.byProject,
    resources: data.resources,
    teams: data.teams,
    activeProjectId: data.activeProjectId && data.projects[data.activeProjectId] ? data.activeProjectId : Object.keys(data.projects)[0] ?? null,
  };
  return {
    version: 3,
    activeWorkspaceId: id,
    workspaces: { [id]: workspace },
    globalUi: data.globalUi,
  };
}

function normalizeChrome(raw: Record<string, ProjectChrome> | undefined): Record<string, ProjectChrome> {
  const out: Record<string, ProjectChrome> = {};
  for (const [id, c] of Object.entries(raw ?? {})) {
    if (!c) continue;
    out[id] = {
      checkpoints: Array.isArray(c.checkpoints) ? c.checkpoints.map((cp) => ({ ...cp, project: normalizeProjectResources(normalizeExecution(cp.project) as Project) })) : [],
      ...(Array.isArray(c.reports) && c.reports.length
        ? {
            reports: c.reports
              .filter((r) => r && r.project && Array.isArray(r.project.tasks) && r.metrics)
              .map((r) => ({ ...r, project: normalizeProjectResources(normalizeExecution(r.project) as Project) })),
          }
        : {}),
      collapsed: Array.isArray(c.collapsed) ? c.collapsed : [],
      ui: c.ui,
      ...(normalizeProjectTab(c.lastTab) ? { lastTab: normalizeProjectTab(c.lastTab)! } : {}),
    };
  }
  return out;
}

export function migrateV1(data: PersistedV1): PersistedV2 {
  const project = data.project;
  if (!project?.id || !Array.isArray(project.tasks)) return emptyCatalog();
  const theme = isThemePref(data.ui?.theme) ? data.ui.theme : "system";
  return {
    version: 2,
    activeProjectId: project.id,
    projects: normalizeProjects({ [project.id]: project }),
    byProject: normalizeChrome({
      [project.id]: {
        checkpoints: data.checkpoints ?? [],
        collapsed: data.collapsed ?? [],
        ui: data.ui,
      },
    }),
    globalUi: { theme, profile: { ...DEFAULT_PROFILE } },
    resources: defaultResources(),
    teams: defaultTeams(),
  };
}

function parseV2(raw: string): PersistedV2 | null {
  try {
    const data = JSON.parse(raw) as PersistedV2;
    if (data?.version !== 2 || !data.projects || typeof data.projects !== "object") return null;
    const projects = normalizeProjects(data.projects);
    const resources = data.resources && Object.keys(data.resources).length > 0 ? data.resources : defaultResources();
    const teams = data.teams && Object.keys(data.teams).length > 0 ? data.teams : defaultTeams();
    return {
      version: 2,
      activeProjectId: data.activeProjectId && projects[data.activeProjectId] ? data.activeProjectId : null,
      projects,
      byProject: normalizeChrome(data.byProject),
      globalUi: normalizeGlobalUi(data.globalUi),
      resources,
      teams,
    };
  } catch {
    return null;
  }
}

function parseV1(raw: string): PersistedV1 | null {
  try {
    const data = JSON.parse(raw) as PersistedV1;
    if (!data.project?.tasks) return null;
    return data;
  } catch {
    return null;
  }
}

function parseV3(raw: string): PersistedV3 | null {
  try {
    const data = JSON.parse(raw) as PersistedV3;
    if (data?.version !== 3 || !data.workspaces || typeof data.workspaces !== "object") return null;
    const workspaces = normalizeWorkspaces(data.workspaces);
    if (Object.keys(workspaces).length === 0) return null;
    const activeWorkspaceId = data.activeWorkspaceId && workspaces[data.activeWorkspaceId] ? data.activeWorkspaceId : Object.keys(workspaces)[0]!;
    return {
      version: 3,
      activeWorkspaceId,
      workspaces,
      globalUi: normalizeGlobalUi(data.globalUi),
      seedRevision: typeof data.seedRevision === "number" ? data.seedRevision : 0,
      sampleJoinedWorkspace: data.sampleJoinedWorkspace === true,
    };
  } catch {
    return null;
  }
}

const SEED_MARKERS: Record<string, { projectId: string; taskId: string; companionId: string; holdTaskId: string; holdDate: string; intakeId?: string }> = {
  ws_wavex: { projectId: "sample-erp", taskId: "x_me_quote", companionId: "sample-erp-quote", holdTaskId: "q_t6", holdDate: "2026-12-01", intakeId: "in_vat" },
  ws_robot: { projectId: "sample-robot", taskId: "rob_x_me_safety", companionId: "sample-robot-safety", holdTaskId: "s_m2", holdDate: "2026-10-20" },
  ws_growth: { projectId: "sample-marketing", taskId: "mkt_x_me_roster", companionId: "sample-mkt-content", holdTaskId: "c_t6", holdDate: "2026-11-16" },
};

function seedWorkspaceIsCurrent(ws: WorkspaceRecord | undefined, id: string): boolean {
  const marker = SEED_MARKERS[id];
  if (!marker) return true;
  const project = ws?.projects[marker.projectId];
  const held = ws?.projects[marker.companionId]?.tasks.find((task) => task.id === marker.holdTaskId);
  const intakeOk = !marker.intakeId || (project?.intakes ?? []).some((item) => item.id === marker.intakeId);
  return !!project?.executionTasks.some((task) => task.id === marker.taskId) && held?.constraint?.date === marker.holdDate && intakeOk;
}

/** 기본 워크스페이스(웨이브엑스·로봇·마케팅)만 최근 목업으로 바꾼다. 이름·색은 두고, 직접 만든 워크스페이스는 둔다. */
function applyLatestSeed(data: PersistedV3): PersistedV3 {
  const stale = SEED_WORKSPACE_META.some((meta) => {
    const current = data.workspaces[meta.id];
    return !!current && !seedWorkspaceIsCurrent(current, meta.id);
  });
  if ((data.seedRevision ?? 0) >= SEED_REVISION && !stale) return data;
  const workspaces = { ...data.workspaces };
  for (const meta of SEED_WORKSPACE_META) {
    const current = workspaces[meta.id];
    if (!current) continue;
    if ((data.seedRevision ?? 0) >= SEED_REVISION && seedWorkspaceIsCurrent(current, meta.id)) continue;
    workspaces[meta.id] = refillWorkspace({ ...current, template: meta.template }, meta.template);
  }
  return { ...data, seedRevision: SEED_REVISION, workspaces };
}

/** 참여 목업이 없고 아직 넣은 적이 없으면 한 번만 더한다. */
function withSampleJoined(data: PersistedV3): PersistedV3 {
  if (data.sampleJoinedWorkspace) return data;
  const id = SAMPLE_JOINED_WORKSPACE.id;
  if (data.workspaces[id]) return { ...data, sampleJoinedWorkspace: true };
  const sample = seedWorkspaceRecords()[id];
  if (!sample) return { ...data, sampleJoinedWorkspace: true };
  return { ...data, sampleJoinedWorkspace: true, workspaces: { ...data.workspaces, [id]: sample } };
}

/** Load v3, or migrate leftover v2/v1. Empty storage gets the seed workspaces. Does not write. */
export function hydrateCatalog(storage: StorageLike): PersistedV3 {
  const v3raw = storage.getItem(STORAGE_V3);
  if (v3raw) {
    const parsed = parseV3(v3raw);
    if (parsed) return withSampleJoined(applyLatestSeed(parsed));
  }
  const v2raw = storage.getItem(STORAGE_V2);
  if (v2raw) {
    const parsed = parseV2(v2raw);
    if (parsed) return withSampleJoined(migrateV2ToV3(parsed));
  }
  const v1raw = storage.getItem(STORAGE_V1);
  if (v1raw) {
    const parsed = parseV1(v1raw);
    if (parsed) return withSampleJoined(migrateV2ToV3(migrateV1(parsed)));
  }
  return seedCatalog();
}

let cache: PersistedV3 | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

export function getCatalogCache(): PersistedV3 {
  if (!cache) cache = typeof localStorage === "undefined" ? seedCatalog() : hydrateCatalog(localStorage);
  return cache;
}

export function replaceCatalogCache(next: PersistedV3) {
  cache = next;
}

export function scheduleCatalogSave(data: PersistedV3, onSaved?: () => void, onError?: (error: unknown) => void) {
  cache = data;
  if (typeof localStorage === "undefined") {
    onSaved?.();
    return;
  }
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      localStorage.setItem(STORAGE_V3, JSON.stringify(data));
      onSaved?.();
    } catch (error) {
      // setItem이 실패해도 이전 저장본은 그대로 남는다(부분 쓰기 없음). 호출한 쪽이 사용자에게 알린다.
      onError?.(error);
    }
  }, 250);
}

/** 대기 중인 저장이 있는지. 종료 직전 flush 판단에 쓴다. */
export function hasPendingCatalogSave(): boolean {
  return saveTimer !== null;
}

export function saveCatalogNow(data: PersistedV3) {
  cache = data;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_V3, JSON.stringify(data));
}
